'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeroSectionProps {
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ className = '' }) => {
  const [currentHeadingIndex, setCurrentHeadingIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const headings = [
  { white: 'Enterprise AI That Delivers', blue: 'Measurable', white2: 'Results' },
  { white: 'Transform Customer Experience with', blue: 'AI-Powered', white2: 'Intelligence' },
  { white: 'Unify Your Business Operations with', blue: 'Intelligent', white2: 'Automation' }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setIsInView(true);},
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentHeadingIndex((prev) => (prev + 1) % headings.length);
        setIsVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`relative bg-white overflow-hidden ${className}`}
      style={{ minHeight: '92vh' }}>

      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pointer-events-none" />

      {/* Decorative geometric shapes */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-[0.04] pointer-events-none">
        <div className="absolute top-20 right-20 w-80 h-80 rounded-full border-[40px] border-[#1bb4d7]" />
        <div className="absolute top-40 right-40 w-40 h-40 rounded-full border-[20px] border-[#1bb4d7]" />
      </div>
      <div className="absolute bottom-0 left-0 w-96 h-96 opacity-[0.03] pointer-events-none">
        <div className="w-full h-full rounded-full border-[60px] border-slate-400" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10 flex flex-col justify-center" style={{ minHeight: '92vh' }}>
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-16 lg:gap-24 items-center py-20 lg:py-4">

          {/* Left Content */}
          <div
            className={`space-y-10 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-[#1bb4d7]/8 rounded-full px-5 py-2.5 border border-[#1bb4d7]/20">
              <Icon name="SparklesIcon" size={16} variant="solid" className="text-[#1bb4d7]" />
              <span className="font-body text-sm font-semibold text-[#1bb4d7] tracking-wide">25+ Years of Transformation Excellence</span>
            </div>

            {/* Animated Heading */}
            <div className="space-y-4">
              <div
                className={`transition-opacity duration-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
                style={{ minHeight: '180px' }}>

                <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight text-slate-900">
                  <span>{headings[currentHeadingIndex].white}</span>
                  {' '}
                  <span className="text-[#1bb4d7]">{headings[currentHeadingIndex].blue}</span>
                  {' '}
                  <span>{headings[currentHeadingIndex].white2}</span>
                </h1>
              </div>

              {/* Indicator Dots */}
              <div className="flex items-center space-x-2">
                {headings.map((_, index) =>
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-500 ${
                  index === currentHeadingIndex ? 'w-8 bg-[#1bb4d7]' : 'w-2 bg-slate-200'}`
                  } />

                )}
              </div>
            </div>

            <p className="font-body text-lg lg:text-xl text-slate-500 leading-relaxed max-w-xl">
              UnifyMax combines 25 years of industry expertise with cutting-edge AI to deliver seamless, intelligent customer experiences across telecom, banking, and retail sectors.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-slate-900 text-white rounded-none font-cta text-base font-semibold hover:bg-[#1bb4d7] transition-all duration-300 group">

                <span>Talk to sales</span>
                <Icon name="ArrowRightIcon" size={18} variant="solid" className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                href="#unifymax-techstack"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-transparent text-slate-900 rounded-none font-cta text-base font-semibold border border-slate-200 hover:border-[#1bb4d7] hover:text-[#1bb4d7] transition-all duration-300">

                <span>Explore Products</span>
                <Icon name="CubeIcon" size={18} variant="outline" />
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6 pt-2 border-t border-slate-100">
              {[
              { icon: 'ShieldCheckIcon', label: 'SOC 2 Certified' },
              { icon: 'CheckBadgeIcon', label: 'ISO 27001' },
              { icon: 'LockClosedIcon', label: 'GDPR Compliant' },
              { icon: 'DocumentCheckIcon', label: 'HIPAA Compliant' }].
              map((badge) =>
              <div key={badge.label} className="flex items-center space-x-1.5">
                  <Icon name={badge.icon as any} size={16} variant="solid" className="text-[#1bb4d7]" />
                  <span className="font-body text-xs text-slate-500 font-medium">{badge.label}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Content - Hero Image */}
          <div
            className={`transition-all duration-1000 delay-300 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            <div className="relative w-full flex items-center justify-center">
              <Image
                src="/assets/images/Frame_2-1774870262064.png"
                alt="UnifyMax enterprise AI platform dashboard showing transformation impact and analytics"
                width={600}
                height={500}
                className="w-full h-auto object-contain"
                priority />

            </div>
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
    </section>);

};

export default HeroSection;